"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, AlertTitle, Container, createTheme, ThemeProvider, Typography, } from "@mui/material";
import { deepmerge } from "@mui/utils";
import { extendedSuperTokenList, } from "@superfluid-finance/tokenlist";
import memoize from "lodash.memoize";
import { nanoid } from "nanoid";
import { useCallback, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { zeroAddress } from "viem";
import { useConnect, useNetwork } from "wagmi";
import { InjectedConnector } from "wagmi/connectors/injected";
import { fromZodError } from "zod-validation-error";
import { checkoutConfigSchema, } from "./CheckoutConfig.js";
import { defaultNetworkAssets, supportedNetworks, } from "./core/index.js";
import { addSuperTokenInfoToPaymentOptions } from "./helpers/addSuperTokenInfoToPaymentOptions.js";
import { filterSuperTokensFromTokenList } from "./helpers/filterSuperTokensFromTokenList.js";
import { mapSupportedNetworksFromPaymentOptions } from "./helpers/mapSupportedNetworksFromPaymentOptions.js";
import { buildThemeOptions } from "./theme.js";
import { WidgetContext } from "./WidgetContext.js";
import { WidgetView } from "./WidgetView.js";
/**
 * The entrypoint to the Superfluid widget.
 */
export function Widget({ productDetails: productDetails_ = {
    name: "",
}, paymentDetails: paymentDetails_, tokenList = extendedSuperTokenList, theme: theme_, walletManager: walletManager_, stepper: stepper_ = { orientation: "vertical" }, eventListeners, type = "page", networkAssets = defaultNetworkAssets, ..._viewProps }) {
    const viewProps = type === "page" ? { type } : { type, ..._viewProps };
    const { connect, connectors } = useConnect();
    const { chains } = useNetwork();
    const walletManager = useMemo(() => {
        if (walletManager_) {
            return walletManager_;
        }
        // Note that there's no good reason to use the default wallet manager in production. It is only to make setting up the widget easier for the _first time_.
        const defaultWalletManager = {
            isOpen: false,
            open: () => connect({
                connector: connectors.find((x) => x.id === "injected") ??
                    new InjectedConnector({
                        chains,
                        options: { shimDisconnect: true },
                    }),
            }),
        };
        return defaultWalletManager;
    }, [walletManager_, connectors, connect, chains]);
    const paymentDetailsInputStringified = JSON.stringify(paymentDetails_);
    const productDetailsInputStringified = JSON.stringify(productDetails_);
    const validationResult = useMemo(() => checkoutConfigSchema.safeParse({
        productDetails: productDetails_,
        paymentDetails: paymentDetails_,
    }), [productDetailsInputStringified, paymentDetailsInputStringified]);
    const { productDetails, paymentDetails, } = useMemo(() => {
        if (validationResult.success) {
            return {
                productDetails: validationResult.data.productDetails,
                paymentDetails: validationResult.data.paymentDetails,
            };
        }
        else {
            // TODO(KK): This should run through the validation as well to automatically populate with default values.
            return {
                productDetails: {
                    name: "",
                },
                paymentDetails: {
                    modifyFlowRateBehaviour: "ADD",
                    paymentOptions: [],
                    defaultWrapAmount: {
                        multiplier: 3,
                        period: "month",
                    },
                },
            };
        }
    }, [validationResult]);
    const { superTokens, underlyingTokens } = useMemo(() => filterSuperTokensFromTokenList(tokenList), [tokenList]); // TODO: Worry about consumer having to keep the token list reference unchanged.
    // TODO: Check if network is configured in wagmi.
    const networks = useMemo(() => mapSupportedNetworksFromPaymentOptions(paymentDetails.paymentOptions), [validationResult]);
    const getSuperToken = useCallback(memoize((address) => {
        const superToken = superTokens.find((x) => x.address.toLowerCase() === address.toLowerCase());
        if (!superToken) {
            throw new Error(`Super Token [${address}] not found from token list (which contains ${superTokens.length} Super Tokens).`);
        }
        return superToken;
    }), [superTokens]);
    const getUnderlyingToken = useCallback(memoize((address) => {
        const underlyingToken = underlyingTokens.find((x) => x.address.toLowerCase() === address.toLowerCase());
        if (!underlyingToken) {
            throw new Error(`Underlying token [${address}] not found from token list (which contains ${underlyingTokens.length} underlying tokens).`);
        }
        return underlyingToken;
    }), [underlyingTokens]);
    const getNetwork = useCallback(memoize((chainId) => {
        const network = supportedNetworks.find((x) => x.id === chainId);
        if (!network) {
            throw new Error("Network not found from supported networks.");
        }
        return network;
    }), []);
    const getNativeAsset = useCallback(memoize((chainId) => {
        const nativeAsset = getNetwork(chainId).nativeCurrency;
        return {
            chainId: chainId,
            address: zeroAddress,
            name: nativeAsset.name,
            decimals: nativeAsset.decimals,
            symbol: nativeAsset.symbol,
        };
    }), [getNetwork]);
    const paymentOptionWithTokenInfoList = useMemo(() => addSuperTokenInfoToPaymentOptions(paymentDetails.paymentOptions, getSuperToken, getUnderlyingToken, getNativeAsset), [
        paymentDetails.paymentOptions,
        getSuperToken,
        getUnderlyingToken,
        getNativeAsset,
    ]);
    const stepper = useMemo(() => ({
        orientation: stepper_.orientation,
    }), [stepper_.orientation]);
    const checkoutState = useMemo(() => ({
        getNetwork,
        getSuperToken,
        getUnderlyingToken,
        getNativeAsset,
        superTokens,
        productDetails,
        paymentDetails,
        tokenList,
        networks,
        paymentOptionWithTokenInfoList,
        walletManager,
        stepper,
        layout: {
            elevated: !["drawer", "dialog"].includes(viewProps.type),
        },
        type: viewProps.type,
        eventListeners: {
            onButtonClick: eventListeners?.onButtonClick ?? NOOP_FUNCTION,
            onRouteChange: eventListeners?.onRouteChange ?? NOOP_FUNCTION,
            onTransactionSent: eventListeners?.onTransactionSent ?? NOOP_FUNCTION,
            onSuccess: eventListeners?.onSuccess ?? NOOP_FUNCTION,
            onSuccessButtonClick: eventListeners?.onSuccessButtonClick ?? NOOP_FUNCTION,
            onPaymentOptionUpdate: eventListeners?.onPaymentOptionUpdate ?? NOOP_FUNCTION,
        },
    }), [
        getNativeAsset,
        getSuperToken,
        getNetwork,
        getNativeAsset,
        superTokens,
        productDetails,
        paymentDetails,
        tokenList,
        networks,
        walletManager,
        stepper,
        viewProps.type,
        eventListeners?.onTransactionSent,
        eventListeners?.onSuccessButtonClick,
        eventListeners?.onRouteChange,
        eventListeners?.onTransactionSent,
        eventListeners?.onSuccess,
        eventListeners?.onPaymentOptionUpdate,
    ]);
    const theme = useMemo(() => {
        const defaultThemeOptions = buildThemeOptions(theme_?.palette?.mode || "light");
        const themeOptions = deepmerge(defaultThemeOptions, theme_);
        return createTheme(themeOptions);
    }, [theme_]);
    // TODO(KK): debug message about what token list is used?
    const paymentDetailsKey = useMemo(() => nanoid(), [paymentDetailsInputStringified]);
    return (_jsx(ErrorBoundary, { fallback: _jsx(Container, { maxWidth: "sm", children: _jsxs(Alert, { severity: "error", variant: "standard", children: [_jsx(AlertTitle, { children: "Oops! The Checkout Widget crashed" }), _jsx("p", { children: "Apologies for any inconvenience caused, but the Checkout Widget just experienced an unexpected problem and failed to load." }), _jsx("p", { children: "We appreciate your understanding and patience. You might want to try reloading the page or come back later. If the issue persists, please don't hesitate to get in touch with us." })] }) }), children: _jsx(WidgetContext.Provider, { value: checkoutState, children: _jsx(ThemeProvider, { theme: theme, children: validationResult.success ? (_jsx(WidgetView, { ...viewProps }, `${viewProps.type}-${paymentDetailsKey}`)) : (_jsxs(Alert, { "data-testid": "widget-error", severity: "error", children: [_jsx(AlertTitle, { children: "Input Error" }), _jsx(Typography, { variant: "inherit", whiteSpace: "pre-wrap", children: fromZodError(validationResult.error, {
                                issueSeparator: "\n",
                            }).message })] })) }) }) }));
}
const NOOP_FUNCTION = () => { };
